import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import { getCompanySettings } from 'modules/settings/stores/company-settings-slice-handler';
import LocalEvent from 'utils/local-event';
import sdkClient from 'utils/sdk-client';
import Log from 'utils/log';
import LayoutManager from '../../../utils/layout-manager';

const initialState = {
    /**
     * @type {AssocArray<MyRulesPackage>}
     */
    packages: {},
    /**
     * @type {string|null}
     */
    selectedPackageUuid: null,
};

class MyRulesPackagesSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!MyRulesPackagesSliceHandler.instance) {
            MyRulesPackagesSliceHandler.instance = new MyRulesPackagesSliceHandler();
        }

        return MyRulesPackagesSliceHandler.instance;
    }

    constructor() {
        super('myRulesPackagesSlice');

        LocalEvent.setListener(LocalEvent.eventTypes.SDK_CLIENT_AUTHENTICATED, (e) => this.onSdkClientAuthenticated(e));
        sdkClient.setSocketEventListener('MY_RULES_PACKAGE_LIST', (e) =>
            this.onPackagesListReceived(e.myRulesPackageList),
        );

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                addPackage(state, action) {
                    state.packages[action.payload.packageUuid] = action.payload;
                },
                removePackage(state, action) {
                    const { id, reason } = action.payload;
                    if (!state.packages[id]) {
                        return;
                    }
                    delete state.packages[id];
                    Log.notice('MY RULES PACKAGES SLICE', `Removing package: ${id}. Reason: ${reason}`);
                    if (state.selectedPackageUuid !== null && state.selectedPackageUuid === id) {
                        state.selectedPackageUuid = null;
                        Log.notice(
                            'MY RULES PACKAGES SLICE',
                            'The selected package has been removed. Will call first controller action.',
                        );
                    }
                    LocalEvent.trigger(LocalEvent.eventTypes.MY_RULES_PACKAGES_LIST_UPDATED);
                },
                setSelectedPackageUuid(state, action) {
                    state.selectedPackageUuid = action.payload;
                },
            },
        });
    }

    onSdkClientAuthenticated() {
        sdkClient.emitSocketEvent('MY_RULES_PACKAGE_LIST_REQUEST');
    }

    /**
     * Manages list of my rules packages received
     * @param {MyRulesPackage[]} receivedPackages
     */
    async onPackagesListReceived(receivedPackages) {
        const receivedIds = [];
        const isDispatcher = getCompanySettings().IsDispatcher;
        Log.debug('MY RULES PACKAGES SLICE', `Received packages list which has ${receivedPackages.length} items`);

        const promises = [];

        receivedPackages.forEach((myRulesPackage) => {
            if (myRulesPackage.webchatForDispatcherOnly && !isDispatcher) {
                return;
            }

            receivedIds.push(myRulesPackage.packageUuid);

            promises.push(this.handlePackage(myRulesPackage));
        });

        await Promise.all(promises).then(() => {
            const currentPackages = this.getOwnState().packages;

            Object.keys(currentPackages).forEach((packageId) => {
                if (receivedIds.indexOf(packageId) < 0) {
                    Log.notice('MY RULES PACKAGES SLICE', `The package: ${packageId} has been removed.`);
                    this.dispatch('removePackage', { id: packageId, reason: 'Package removed externally.' });
                }
            });

            // select first package
            const selectedPackage = this.getOwnState().selectedPackageUuid;
            if (selectedPackage === null && receivedIds.length > 0) {
                this.dispatch('setSelectedPackageUuid', receivedIds[0]);
            }

            LocalEvent.trigger(LocalEvent.eventTypes.MY_RULES_PACKAGES_LIST_UPDATED);
        });
    }

    /**
     * Checks whether a package should be added or removed from the store
     * @param {MyRulesPackage} myRulesPackage
     */
    handlePackage = (myRulesPackage) =>
        new Promise((resolve) => {
            if (myRulesPackage.webchatUri.length === 0) {
                Log.warn('MY RULES PACKAGES SLICE', 'Discarding the package.');
                this.dispatch('removePackage', {
                    id: myRulesPackage.packageUuid,
                    reason: 'Empty webchatUri received.',
                });

                resolve();
                return;
            }

            let label = myRulesPackage.label;
            let logoContent = null;
            let savedPackage = null;
            const language = LayoutManager.getLanguage();

            if (myRulesPackage.translatedLabels != null) {
                const translatedLabels = JSON.parse(myRulesPackage.translatedLabels);
                if (
                    translatedLabels !== null &&
                    typeof translatedLabels === 'object' &&
                    typeof translatedLabels[language] !== 'undefined' &&
                    translatedLabels[language].length > 0
                ) {
                    label = translatedLabels[language];
                }
            }

            webchatSDK.STWAPI.getPackage(myRulesPackage.packageUuid)
                .then((packageData) => {
                    logoContent = packageData.data.results.UserLogoContent;
                    Log.notice('MY RULES PACKAGES SLICE', `Saving package ${myRulesPackage.packageUuid} data to store`);
                })
                .catch((e) => {
                    Log.error(
                        'MY RULES PACKAGES SLICE',
                        `The data of package ${myRulesPackage.packageUuid} could not be fetched. Error: ${JSON.stringify(e)}`,
                    );
                })
                .finally(() => {
                    savedPackage = {
                        ...myRulesPackage,
                        TranslatedLabel: label,
                        UserLogoContent: logoContent ? `data:image/png;base64, ${logoContent}` : null,
                    };
                    this.dispatch('addPackage', savedPackage);

                    resolve();
                });
        });

    selectPackage(id) {
        const selectedPackageUuid = this.getOwnState().selectedPackageUuid;

        if (selectedPackageUuid === id) {
            return;
        }

        this.dispatch('setSelectedPackageUuid', id);

        LocalEvent.trigger(LocalEvent.eventTypes.MY_RULES_PACKAGE_SELECTED, id);
    }

    packageExists(id) {
        return !!this.getOwnState().packages[id];
    }

    getPackage(id) {
        if (!this.getOwnState().packages[id]) {
            return null;
        }

        return this.getOwnState().packages[id];
    }

    getSelectedPackageUuid() {
        return this.getOwnState().selectedPackageUuid;
    }
}

export const myRulesPackagesSliceHandler = MyRulesPackagesSliceHandler.getInstance();
export const selectMyRulesPackages = (state) => state.myRulesPackagesSlice.packages;
export const selectSelectedPackageUuid = (state) => state.myRulesPackagesSlice.selectedPackageUuid;
export const { selectPackage, packageExists, getPackage, getSelectedPackageUuid } = myRulesPackagesSliceHandler;
